import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _decorate from "@babel/runtime/helpers/esm/decorate";
import _getPrototypeOf from "@babel/runtime/helpers/esm/getPrototypeOf";
import _get from "@babel/runtime/helpers/esm/get";
let _ = t => t,
  _t,
  _t2,
  _t3,
  _t4;
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _superPropGet(t, e, r, o) { var p = _get(_getPrototypeOf(1 & o ? t.prototype : t), e, r); return 2 & o ? function (t) { return p.apply(r, t); } : p; }
/**
 * @license
 *
 * Copyright IBM Corp. 2020, 2024
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { html, LitElement } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { EXPRESSIVE_MODAL_MODE, EXPRESSIVE_MODAL_SIZE } from "./defs.js";
import { classMap } from 'lit/directives/class-map.js';
import settings from '@carbon/ibmdotcom-utilities/es/utilities/settings/settings.js';
import HostListener from '@carbon/web-components/es/globals/decorators/host-listener.js';
import HostListenerMixin from '@carbon/web-components/es/globals/mixins/host-listener.js';
import on from '@carbon/web-components/es/globals/mixins/on.js';
import { selectorTabbable } from '@carbon/web-components/es/globals/settings.js';
import StableSelectorMixin from "../../globals/mixins/stable-selector.js";
import styles from "././expressive-modal.css.js";
import { carbonElement as customElement } from '@carbon/web-components/es/globals/decorators/carbon-element.js';
const {
  prefix,
  stablePrefix: c4dPrefix
} = settings;

/* eslint-disable no-bitwise */
const PRECEDING = Node.DOCUMENT_POSITION_PRECEDING | Node.DOCUMENT_POSITION_CONTAINS;
const FOLLOWING = Node.DOCUMENT_POSITION_FOLLOWING | Node.DOCUMENT_POSITION_CONTAINED_BY;
const WITHIN = Node.DOCUMENT_POSITION_CONTAINED_BY;
/* eslint-enable no-bitwise */

// TODO: Wait for `.d.ts` update to support `ResizeObserver`
// @ts-ignore
const onResize = ([entry]) => {
  const {
    target,
    contentRect
  } = entry;
  const {
    width,
    height
  } = contentRect;
  const modalContent = target;
  modalContent.style.setProperty('--modal-vh', `${height}px`);
  modalContent.style.setProperty('--modal-vw', `${width}px`);
};

/**
 * The table mapping slot name with the private property name that indicates the existence of the slot content.
 */
const slotExistencePropertyNames = {
  header: '_hasHeader',
  footer: '_hasFooter'
};

/**
 * Expressive modal.
 *
 * @element c4d-expressive-modal
 * @fires c4d-expressive-modal-beingclosed
 *   The custom event fired before this modal is being closed upon a user gesture.
 *   Cancellation of this event stops the user-initiated action of closing this modal.
 * @fires c4d-expressive-modal-closed - The custom event fired after this modal is closed upon a user gesture.
 * @slot header - The header content.
 * @slot footer - The footer content.
 * @csspart modal-header - The modal header. Usage: `c4d-expressive-modal::part(modal-header)`
 * @csspart modal-body - The modal body. Usage: `c4d-expressive-modal::part(modal-body)`
 * @csspart sentinel-button - Taget all Sentinels buttons Usage: `c4d-expressive-modal::part(sentinel-button)`
 * @csspart sentinel-button--start - Button labled START. Usage: `c4d-expressive-modal::part(sentinel-button--start)`
 * @csspart sentinel-button--end - Button labled END. Usage: `c4d-expressive-modal::part(sentinel-button--end)`
 * @csspart modal-container - The modal container. Usage: `c4d-expressive-modal::part(modal-container)`
 * @csspart footer-container - The footer container. Usage: `c4d-expressive-modal::part(footer-container)`
 */
let C4DExpressiveModal = _decorate([customElement(`${c4dPrefix}-expressive-modal`)], function (_initialize, _StableSelectorMixin) {
  class C4DExpressiveModal extends _StableSelectorMixin {
    constructor(...args) {
      super(...args);
      _initialize(this);
    }
  }
  return {
    F: C4DExpressiveModal,
    d: [{
      kind: "field",
      decorators: [state()],
      key: "_hasHeader",
      value() {
        return false;
      }
    }, {
      kind: "field",
      decorators: [state()],
      key: "_hasBody",
      value() {
        return false;
      }
    }, {
      kind: "field",
      decorators: [state()],
      key: "_hasFooter",
      value() {
        return false;
      }
    }, {
      kind: "field",
      key: "_launcher",
      value() {
        return null;
      }
    }, {
      kind: "field",
      key: "hasFocusableElements",
      value() {
        return [this];
      }
    }, {
      kind: "get",
      key: "focusableElements",
      value:
      /**
       * `true` if there is a header content.
       */

      /**
       * `true` if there is a body content.
       */

      /**
       * `true` if there is a footer content.
       */

      /**
       * The element that had focus before this modal gets open.
       */

      /**
       * Collection of elements to search for focusable elements.
       */

      /**
       * Returns all focusable elements within this component and its shadowroot
       */
      function () {
        var _this$shadowRoot;
        const {
          selectorCloseButton,
          selectorTabbable: selectorTabbableForModal
        } = this.constructor;
        return [...Array.from(((_this$shadowRoot = this.shadowRoot) === null || _this$shadowRoot === void 0 ? void 0 : _this$shadowRoot.querySelectorAll(selectorCloseButton)) || []), ...Array.from(this.querySelectorAll(selectorTabbableForModal))];
      }
    }, {
      kind: "get",
      key: "_focusableElements",
      value: function () {
        const {
          hasFocusableElements
        } = this;
        const focusableElements = [];
        hasFocusableElements.forEach(el => {
          if (el.focusableElements) {
            focusableElements.push(...el.focusableElements);
          }
        });
        return Array.from(new Set(focusableElements)).sort((a, b) => {
          const comparison = a.compareDocumentPosition(b);

          /* eslint-disable no-bitwise */
          if (comparison & PRECEDING) {
            return 1;
          }
          if (comparison & FOLLOWING) {
            return -1;
          }
          /* eslint-enable no-bitwise */

          return 0;
        });
      }

      /**
       * Node to track focus going outside of modal content.
       */
    }, {
      kind: "field",
      decorators: [query('#start-sentinel')],
      key: "_startSentinelNode",
      value: void 0
    }, {
      kind: "field",
      decorators: [query('#end-sentinel')],
      key: "_endSentinelNode",
      value: void 0
    }, {
      kind: "field",
      decorators: [query(`.${prefix}--modal-content`)],
      key: "modalContent",
      value: void 0
    }, {
      kind: "field",
      decorators: [query(`.${c4dPrefix}-ce--modal__body`)],
      key: "modalBody",
      value: void 0
    }, {
      kind: "field",
      key: "_resizeObserver",
      value() {
        return new ResizeObserver(onResize);
      }
    }, {
      kind: "field",
      decorators: [HostListener('click')],
      key: "_handleClick",
      value() {
        return event => {
          if (event.composedPath().indexOf(this.shadowRoot) < 0) {
            this._handleUserInitiatedClose(event.target);
          }
        };
      }
    }, {
      kind: "field",
      key: "_handleFocusIn",
      value() {
        return ({
          target,
          relatedTarget
        }) => {
          const {
            tryFocusElems
          } = this.constructor;
          let focusFromWithin = false;
          if (target && relatedTarget) {
            const comparedToThis = this.compareDocumentPosition(relatedTarget);
            const comparedToShadowRoot = this.shadowRoot.compareDocumentPosition(relatedTarget);
            // If relatedTarget is descendent of `this` or `this.shadowRoot`.
            if (comparedToThis & WITHIN || comparedToShadowRoot & WITHIN) {
              focusFromWithin = true;
            }
          }
          const {
            _endSentinelNode: endSentinelNode,
            _startSentinelNode: startSentinelNode,
            _focusableElements: focusableElements
          } = this;
          if (focusFromWithin) {
            if (target === startSentinelNode) {
              tryFocusElems(focusableElements, true, this);
            } else if (target === endSentinelNode) {
              tryFocusElems(focusableElements, false, this);
            }
          } else {
            tryFocusElems(focusableElements, false, this);
          }
        };
      }
    }, {
      kind: "field",
      decorators: [HostListener('focusout')],
      key: "_handleFocusOut",
      value() {
        return ({
          target,
          relatedTarget
        }) => {
          var _this$shadowRoot2;
          // Don't attempt to wrap focus if the modal isn't open.
          if (!this.open) {
            return;
          }
          // If no target/relatedTarget, focus has entered/left the window. Do nothing.
          if (!target || !relatedTarget) {
            return;
          }
          const {
            tryFocusElems
          } = this.constructor;
          const {
            _focusableElements: focusableElements
          } = this;

          // See if element gaining focus is inside `this` or `this.shadowRoot`.
          const positionToModal = this.compareDocumentPosition(relatedTarget) | (((_this$shadowRoot2 = this.shadowRoot) === null || _this$shadowRoot2 === void 0 ? void 0 : _this$shadowRoot2.compareDocumentPosition(relatedTarget)) || 0);
          const positionToPrevious = target.compareDocumentPosition(relatedTarget);
          const relatedTargetIsContained = Boolean(positionToModal & WITHIN);

          // If focusing outside of `this`, cycle focus
          if (!relatedTargetIsContained && !(relatedTarget === this)) {
            if (positionToPrevious & PRECEDING) {
              tryFocusElems(focusableElements, true, this);
            } else if (positionToPrevious & FOLLOWING) {
              tryFocusElems(focusableElements, false, this);
            }
          }
        };
      }
    }, {
      kind: "field",
      decorators: [HostListener('document:keydown')],
      key: "_handleKeydown",
      value() {
        return ({
          key,
          target
        }) => {
          if (key === 'Esc' || key === 'Escape') {
            this._handleUserInitiatedClose(target);
          }
        };
      }
    }, {
      kind: "method",
      key: "_handleClickContainer",
      value:
      /**
       * Node to track focus going outside of modal content.
       */
      // TODO: Wait for `.d.ts` update to support `ResizeObserver`
      // @ts-ignore
      /**
       * Handles `click` event on this element.
       *
       * @param event The event.
       */
      /* eslint-disable no-bitwise */
      /**
       * Handles the `focusin` event on the start and end sentinels
       *
       * @param event The event.
       * @param event.target The event target.
       * @param event.relatedTarget The event relatedTarget.
       */
      /**
       * Handles `focusout` event on this element.
       *
       * @param event The event.
       * @param event.target The event target.
       * @param event.relatedTarget The event relatedTarget.
       */
      /* eslint-enable no-bitwise */
      /**
       * Handles `click` event on the modal container.
       *
       * @param event The event.
       */
      function _handleClickContainer(event) {
        if (event.target.matches(this.constructor.selectorCloseButton)) {
          this._handleUserInitiatedClose(event.target);
        }
      }

      /**
       * Handles `slotchange` event.
       */
    }, {
      kind: "method",
      key: "_handleSlotChange",
      value: function _handleSlotChange({
        target
      }) {
        const {
          name
        } = target;
        const hasContent = target.assignedNodes().some(node => {
          // Allow non-empty text nodes.
          if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
            return true;
          }
          // Allow only element nodes that don't have a .cds--visually-hidden
          // class.
          if (node instanceof Element) {
            return !node.classList.contains('cds--visually-hidden');
          }
          // No opinion on other cases.
          return true;
        });
        this[slotExistencePropertyNames[name] || '_hasBody'] = hasContent;
      }

      /**
       * Handles user-initiated close request of this modal.
       *
       * @param triggeredBy The element that triggered this close request.
       */
    }, {
      kind: "method",
      key: "_handleUserInitiatedClose",
      value: function _handleUserInitiatedClose(triggeredBy) {
        if (this.open) {
          const init = {
            bubbles: true,
            cancelable: true,
            composed: true,
            detail: {
              triggeredBy
            }
          };
          const {
            eventBeforeClose,
            eventClose
          } = this.constructor;
          if (this.dispatchEvent(new CustomEvent(eventBeforeClose, init))) {
            this.open = false;
            this.dispatchEvent(new CustomEvent(eventClose, init));
          }
          this.setAttribute('aria-hidden', 'true');
        }
      }

      /**
       * @param timeout The number of milliseconds as the longest time waiting for `transitionend` event.
       * @returns A promise that is resolves when `transitionend` on the host element fires.
       */
    }, {
      kind: "method",
      key: "_waitForTransitionEnd",
      value: function _waitForTransitionEnd(timeout = 1000) {
        return new Promise(resolve => {
          let done = false;
          let hTransitionEnd;
          const handleResolve = () => {
            if (hTransitionEnd) {
              hTransitionEnd.release();
              hTransitionEnd = null;
            }
            if (!done) {
              resolve(undefined);
              done = true;
            }
          };
          on(this, 'transitionend', handleResolve);
          setTimeout(handleResolve, timeout);
        });
      }

      /**
       * @returns The header content.
       */
    }, {
      kind: "method",
      key: "_renderHeader",
      value: function _renderHeader() {
        const {
          _hasHeader: hasHeader,
          _hasBody: hasBody,
          _hasFooter: hasFooter
        } = this;
        const headerClasses = classMap({
          [`${c4dPrefix}-ce--modal__header--with-body`]: hasHeader && (hasBody || hasFooter)
        });
        return html(_t || (_t = _`
      <div
        id="${0}--modal-header"
        class="${0}"
        part="modal-header">
        <slot name="header"></slot>
      </div>
    `), prefix, headerClasses);
      }

      /**
       * @returns The body content.
       */
    }, {
      kind: "method",
      key: "_renderBody",
      value: function _renderBody() {
        const {
          _hasBody: hasBody,
          _hasFooter: hasFooter
        } = this;
        const bodyClasses = classMap({
          [`${c4dPrefix}-ce--modal__body`]: true,
          [`${c4dPrefix}-ce--modal__body--with-footer`]: hasBody && hasFooter
        });
        return html(_t2 || (_t2 = _`
      <div class="${0}" part="modal-body"><slot></slot></div>
    `), bodyClasses);
      }

      /**
       * @returns The footer content.
       */
      // eslint-disable-next-line class-methods-use-this
    }, {
      kind: "method",
      key: "_renderFooter",
      value: function _renderFooter() {
        return html(_t3 || (_t3 = _`
      <div part="footer-container"><slot name="footer"></slot></div>
    `));
      }

      /**
       * The additional CSS class names for the container <div> of the element.
       */
    }, {
      kind: "field",
      decorators: [property({
        attribute: 'container-class'
      })],
      key: "containerClass",
      value() {
        return '';
      }
    }, {
      kind: "field",
      decorators: [property({
        type: Boolean,
        reflect: true
      })],
      key: "open",
      value() {
        return false;
      }
    }, {
      kind: "field",
      decorators: [property({
        reflect: true,
        attribute: 'expressive-size'
      })],
      key: "size",
      value() {
        return EXPRESSIVE_MODAL_SIZE.REGULAR;
      }
    }, {
      kind: "field",
      decorators: [property({
        reflect: true,
        attribute: 'mode'
      })],
      key: "mode",
      value() {
        return EXPRESSIVE_MODAL_MODE.DEFAULT;
      }
    }, {
      kind: "method",
      key: "render",
      value:
      /**
       * `true` if the modal should be open.
       */
      /**
       * The size variant.
       */
      /**
       * The mode variant.
       */
      function render() {
        const {
          size,
          _handleClickContainer: handleClickContainer,
          _handleSlotChange: handleSlotChange,
          _handleFocusIn: handleFocusIn
        } = this;
        const containerClass = this.containerClass.split(' ').filter(Boolean).reduce((acc, item) => _objectSpread(_objectSpread({}, acc), {}, {
          [item]: true
        }), {});
        const containerClasses = classMap(_objectSpread({
          [`${prefix}--modal-container`]: true,
          [`${prefix}--modal-container--fullwidth`]: size === EXPRESSIVE_MODAL_SIZE.FULL_WIDTH
        }, containerClass));
        return html(_t4 || (_t4 = _`
      <button
        id="start-sentinel"
        class="${0}--visually-hidden"
        part="sentinel-button sentinel-button--start"
        @focusin="${0}">
        START
      </button>
      <div
        class="${0}"
        part="modal-container"
        tabindex="-1"
        role="dialog"
        aria-labelledby="${0}--modal-header"
        @click="${0}"
        @slotchange="${0}">
        <div class="${0}--modal-content" part="modal-content">
          ${0}${0}${0}
        </div>
      </div>
      <button
        id="end-sentinel"
        class="${0}--visually-hidden"
        part="sentinel-button sentinel-button--end"
        @focusin="${0}">
        END
      </button>
    `), prefix, handleFocusIn, containerClasses, c4dPrefix, handleClickContainer, handleSlotChange, prefix, this._renderHeader(), this._renderBody(), this._renderFooter(), prefix, handleFocusIn);
      }
    }, {
      kind: "method",
      key: "firstUpdated",
      value: function firstUpdated() {
        if (this.modalContent) {
          this._resizeObserver.observe(this.modalContent);
        }
      }
    }, {
      kind: "method",
      key: "updated",
      value: async function updated(changedProperties) {
        const {
          _focusableElements: focusableElements,
          size
        } = this;
        const {
          selectorCloseButton,
          tryFocusElems
        } = this.constructor;
        if (changedProperties.has('size')) {
          const closeButton = this.querySelector(selectorCloseButton);
          if (closeButton) {
            closeButton.size = size;
          }
        }
        if (changedProperties.has('open')) {
          if (this.open) {
            this.ownerDocument.body.style.overflow = 'hidden';
            this.removeAttribute('aria-hidden');
            this._launcher = this.ownerDocument.activeElement;
            await this._waitForTransitionEnd();
            const primaryFocusNode = this.querySelector(this.constructor.selectorPrimaryFocus);
            if (primaryFocusNode) {
              // For cases where a `carbon-web-components` component (e.g. `<cds-btn>`) being `primaryFocusNode`,
              // where its first update/render cycle that makes it focusable happens after `<cds-modal>`'s first update/render cycle
              primaryFocusNode.focus();
            } else {
              tryFocusElems(focusableElements, true, this);
            }
          } else if (this._launcher && typeof this._launcher.focus === 'function') {
            this._launcher.focus();
            this.ownerDocument.body.style.overflow = '';
            this._launcher = null;
          }
        }
        return _superPropGet(C4DExpressiveModal, "updated", this, 3)([changedProperties]);
      }

      /**
       * A selector selecting buttons that should close this modal.
       */
    }, {
      kind: "get",
      static: true,
      key: "selectorCloseButton",
      value: function () {
        return `[data-modal-close],${prefix}-modal-close-button,${c4dPrefix}-expressive-modal-close-button`;
      }

      /**
       * A selector selecting tabbable nodes.
       */
    }, {
      kind: "get",
      static: true,
      key: "selectorTabbable",
      value: function () {
        return `
      ${selectorTabbable},
      ${c4dPrefix}-button,
      ${c4dPrefix}-expressive-modal,
      ${c4dPrefix}-expressive-modal-close-button
    `;
      }

      /**
       * A selector selecting the nodes that should be focused when modal gets open.
       */
    }, {
      kind: "get",
      static: true,
      key: "selectorPrimaryFocus",
      value: function () {
        return `
      [data-modal-primary-focus],
      ${c4dPrefix}-expressive-modal-footer ${c4dPrefix}-button[kind="primary"]
    `;
      }
    }, {
      kind: "get",
      static: true,
      key: "stableSelector",
      value: function () {
        return `${c4dPrefix}--expressive-modal`;
      }

      /**
       * The name of the custom event fired before this modal is being closed upon a user gesture.
       * Cancellation of this event stops the user-initiated action of closing this modal.
       */
    }, {
      kind: "get",
      static: true,
      key: "eventBeforeClose",
      value: function () {
        return `${c4dPrefix}-expressive-modal-beingclosed`;
      }

      /**
       * The name of the custom event fired after this modal is closed upon a user gesture.
       */
    }, {
      kind: "get",
      static: true,
      key: "eventClose",
      value: function () {
        return `${c4dPrefix}-expressive-modal-closed`;
      }

      /**
       * Tries to focus on the given elements and bails out if one of the is successful.
       *
       * @param elems The elements.
       * @param reverse `true` to go through the list in reverse order.
       * @param fallback element to focus on if none
       * @returns `true` if one of the attempts is successful, `false` otherwise.
       */
    }, {
      kind: "method",
      static: true,
      key: "tryFocusElems",
      value: function tryFocusElems(elems, reverse = false, fallback = null) {
        if (!reverse) {
          for (let i = 0; i < elems.length; ++i) {
            const elem = elems[i];
            if (elem.offsetWidth || elem.offsetHeight || elem.getClientRects().length) {
              elem.focus();
              if (elem.getRootNode().activeElement === elem) {
                return true;
              }
            }
          }
        } else {
          for (let i = elems.length - 1; i >= 0; --i) {
            const elem = elems[i];
            if (elem.offsetWidth || elem.offsetHeight || elem.getClientRects().length) {
              elem.focus();
              if (elem.getRootNode().activeElement === elem) {
                return true;
              }
            }
          }
        }
        fallback === null || fallback === void 0 || fallback.focus();
        return false;
      }
    }, {
      kind: "field",
      static: true,
      key: "styles",
      value() {
        return styles;
      }
    }]
  };
}, StableSelectorMixin(HostListenerMixin(LitElement)));
/* @__GENERATE_REACT_CUSTOM_ELEMENT_TYPE__ */
export default C4DExpressiveModal;
//# sourceMappingURL=expressive-modal.js.map
